/** Osano consent categories, as returned by `Osano.cm.getConsent()`. */
export type OsanoConsentCategory =
  | "ESSENTIAL"
  | "STORAGE"
  | "MARKETING"
  | "PERSONALIZATION"
  | "ANALYTICS"
  | "OPT_OUT";

export type OsanoConsent = Partial<
  Record<OsanoConsentCategory, "ACCEPT" | "DENY">
>;

/** The slice of `window.Osano.cm` this helper uses. */
export interface OsanoConsentManager {
  getConsent(): OsanoConsent;
  addEventListener(
    event: string,
    listener: (consent: OsanoConsent) => void
  ): void;
  removeEventListener(
    event: string,
    listener: (consent: OsanoConsent) => void
  ): void;
}

declare global {
  interface Window {
    Osano?: { cm?: OsanoConsentManager };
  }
}

export interface OsanoConsentObserver {
  disconnect(): void;
}

/**
 * `osano-cm-initialized` fires once Osano has resolved the visitor's stored
 * choice and jurisdiction; Osano fires it immediately for a listener added
 * after that point. Before it, `getConsent()` holds defaults, so nothing is
 * reported until then. The other two fire when the banner is answered and
 * when the drawer changes a choice.
 */
const INITIALIZED_EVENT = "osano-cm-initialized";
const CHANGE_EVENTS = [
  "osano-cm-consent-saved",
  "osano-cm-consent-changed",
] as const;

/**
 * The Osano script is loaded `beforeInteractive`, so `window.Osano.cm` is
 * normally there before any React effect runs. The poll covers a slow CDN; a
 * blocked script never reports, which keeps consent-gated tools off.
 */
const POLL_INTERVAL_MS = 500;
const POLL_BUDGET_MS = 30_000;

/**
 * Reports whether the visitor has granted an Osano consent category: once
 * when Osano has initialized, then on every change. Reports only on change.
 */
export function observeOsanoConsent(
  category: OsanoConsentCategory,
  onChange: (granted: boolean) => void
): OsanoConsentObserver {
  let manager: OsanoConsentManager | undefined;
  let lastGranted: boolean | undefined;
  let poll: ReturnType<typeof setInterval> | undefined;

  /**
   * Osano is a third-party script and these run inside React effects, where
   * an exception unmounts the page. A misbehaving Osano must only cost the
   * consent-gated tool, never the page, so nothing here is allowed to throw.
   */
  const guarded = (run: () => void) => {
    try {
      run();
    } catch {
      // Consent stays unreported: the gated tool remains off for this visitor.
    }
  };

  const report = () =>
    guarded(() => {
      const granted = manager?.getConsent()?.[category] === "ACCEPT";
      if (granted === lastGranted) return;
      lastGranted = granted;
      onChange(granted);
    });

  const attach = (cm: OsanoConsentManager) => {
    manager = cm;
    guarded(() => {
      CHANGE_EVENTS.forEach((event) => cm.addEventListener(event, report));
      // Last: Osano calls this back synchronously when already initialized.
      cm.addEventListener(INITIALIZED_EVENT, report);
    });
  };

  if (window.Osano?.cm) {
    attach(window.Osano.cm);
  } else {
    let waited = 0;
    poll = setInterval(() => {
      waited += POLL_INTERVAL_MS;
      const cm = window.Osano?.cm;
      if (cm) {
        clearInterval(poll);
        attach(cm);
      } else if (waited >= POLL_BUDGET_MS) {
        clearInterval(poll);
      }
    }, POLL_INTERVAL_MS);
  }

  return {
    disconnect() {
      clearInterval(poll);
      guarded(() => {
        manager?.removeEventListener(INITIALIZED_EVENT, report);
        CHANGE_EVENTS.forEach((event) =>
          manager?.removeEventListener(event, report)
        );
      });
    },
  };
}
